<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
  options: { type: Array, default: () => [] },
  // Always an array, even for one choice — the whole point is more than one.
  modelValue: { type: Array, default: () => [] },
  label: { type: String, default: '' },
  // What the control reads when nothing is chosen; with choices it counts them.
  placeholder: { type: String, default: 'Any' },
  searchable: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  ariaLabel: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue', 'change']);

const root = ref(null);
const open = ref(false);
const query = ref('');

const choices = computed(() => props.options.map((option) => (
  typeof option === 'object' && option !== null
    ? { value: option.value, label: option.label }
    : { value: option, label: option }
)));

const visible = computed(() => {
  const q = query.value.trim().toLowerCase();
  return q ? choices.value.filter((c) => String(c.label).toLowerCase().includes(q)) : choices.value;
});

const isChosen = (value) => props.modelValue.some((v) => String(v) === String(value));

const summary = computed(() => {
  const chosen = props.modelValue.length;
  if (!chosen) return props.placeholder;
  if (chosen === 1) {
    return choices.value.find((c) => String(c.value) === String(props.modelValue[0]))?.label ?? '1 selected';
  }
  return `${chosen} selected`;
});

const toggleChoice = (choice) => {
  const next = isChosen(choice.value)
    ? props.modelValue.filter((v) => String(v) !== String(choice.value))
    : [...props.modelValue, choice.value];

  emit('update:modelValue', next);
  emit('change', next);
};

const clear = () => {
  emit('update:modelValue', []);
  emit('change', []);
};

const close = () => { open.value = false; query.value = ''; };

const onDocumentClick = (event) => {
  if (open.value && root.value && !root.value.contains(event.target)) close();
};

const onKeydown = (event) => {
  if (event.key === 'Escape' && open.value) { event.preventDefault(); close(); }
};

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <div ref="root" class="xl-multiselect" :class="{ 'is-open': open, 'is-disabled': props.disabled }">
    <span v-if="props.label" class="xl-multiselect__label">{{ props.label }}</span>

    <button
      type="button"
      class="xl-multiselect__control"
      :disabled="props.disabled"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :aria-label="props.ariaLabel || props.label || undefined"
      @click="open = !open"
    >
      <span :class="{ 'xl-multiselect__placeholder': !props.modelValue.length }">{{ summary }}</span>
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M5 7.5 10 12.5 15 7.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <div v-if="open" class="xl-multiselect__menu" role="listbox" aria-multiselectable="true">
      <input
        v-if="props.searchable"
        v-model="query"
        class="xl-multiselect__search"
        type="text"
        placeholder="Type to filter"
        aria-label="Filter the options"
      />

      <p v-if="!visible.length" class="xl-multiselect__empty">Nothing matches that.</p>

      <label
        v-for="choice in visible"
        :key="choice.value"
        class="xl-multiselect__option"
        :class="{ 'is-chosen': isChosen(choice.value) }"
      >
        <input type="checkbox" :checked="isChosen(choice.value)" @change="toggleChoice(choice)" />
        <span>{{ choice.label }}</span>
      </label>

      <button v-if="props.modelValue.length" type="button" class="xl-multiselect__clear" @click="clear">
        Clear {{ props.modelValue.length }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.xl-multiselect, .xl-multiselect * { box-sizing: border-box; }

.xl-multiselect { position: relative; display: inline-flex; flex-direction: column; gap: 6px; font-family: var(--xl-font-family); }

/* Same label scale as XlField: the two sit in one column on the same forms,
   and a multiselect whose label is a step smaller reads as a lesser field. */
.xl-multiselect__label { font-size: var(--xl-type-label-large-size); line-height: var(--xl-type-label-large-line); font-weight: 500; color: var(--xl-color-secondary-text); cursor: default; }

.xl-multiselect__control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  height: 42px;
  min-width: 150px;
  padding: 0 12px 0 14px;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-sm);
  background: #fff;
  font: inherit;
  font-size: var(--xl-font-size-base);
  color: var(--xl-color-primary-text);
  cursor: pointer;
}

.xl-multiselect__control:hover:not(:disabled) { border-color: var(--xl-color-primary-fixed-dim); }
.xl-multiselect__control:focus-visible { outline: none; border-color: var(--xl-color-primary); box-shadow: 0 0 0 3px rgba(94, 47, 120, 0.1); }
.xl-multiselect.is-open .xl-multiselect__control { border-color: var(--xl-color-primary); }

.xl-multiselect__placeholder { color: var(--xl-color-outline); }

.xl-multiselect__menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 30;
  min-width: 100%;
  width: max-content;
  max-width: 320px;
  max-height: 264px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-sm);
  background: #fff;
  box-shadow: var(--xl-shadow-2);
}

.xl-multiselect__search {
  width: 100%;
  height: 34px;
  margin-bottom: 6px;
  padding: 0 10px;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-sm);
  font: inherit;
  font-size: var(--xl-type-body-medium-size);
}

.xl-multiselect__option {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 10px;
  border-radius: var(--xl-radius-sm);
  font-size: var(--xl-font-size-base);
  color: var(--xl-color-primary-text);
  white-space: nowrap;
  cursor: pointer;
}

.xl-multiselect__option:hover { background: var(--xl-color-primary-container); }
.xl-multiselect__option.is-chosen { color: var(--xl-color-primary); font-weight: 600; }
.xl-multiselect__option input { accent-color: var(--xl-color-primary); }

.xl-multiselect__empty { margin: 0; padding: 10px; font-size: var(--xl-type-body-medium-size); color: var(--xl-color-outline); }

.xl-multiselect__clear {
  display: block;
  width: 100%;
  margin-top: 4px;
  padding: 8px;
  border: 0;
  border-top: 1px solid var(--xl-color-border);
  background: transparent;
  font: inherit;
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-primary);
  cursor: pointer;
}
</style>
